import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Icon, Input, Menu, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { convertImage } from '../../../../../services/contents';
import AspectRatioScreenContent from '../../../../shared/components/AspectRatioScreenContent';

const TABS = ['all', 'pending', 'covered'];
const MAX_INLINE_VERSION_PILLS = 3;

const PreviewFrameHolder = styled.div`
  width: 100%;
  height: 100%;
  .contentRectangle {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
  }
  .alignImage {
    display: flex;
    height: 100%;
    width: 100%;
    justify-content: center;
    background-color: #000;
  }
  .contentRectangleCardImage {
    width: 100%;
    object-fit: scale-down;
  }
  .rectangleNoContent {
    border: 1px dashed #00000073;
    background: transparent;
  }
`;

const ResolutionsPanelComponent = ({
  listResolution = [],
  activeVersion,
  versions = [],
  onSelectVersion,
  onUnlinkContent,
  onRelinkContent,
  onPreviewContent,
  versionContents = [],
  isPaymentStatusApproved = false,
  isTheLastOfTheRelationship,
  hasDifferentResolution = false,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    return listResolution.map((relation) => {
      const contentsForVersion = (relation.contents || []).filter(
        (c) => (c.content_version_name || 'V1') === activeVersion
      );
      const assignedContent = contentsForVersion.length > 0 ? contentsForVersion[0] : null;
      const relationDisplayIds = (relation.displays || []).map((d) => d.id);
      const excludedContent = !assignedContent
        ? versionContents.find((c) => (c.excludedDisplayIds || []).some((id) => relationDisplayIds.includes(id)))
        : null;
      const assignedVersions = versions.filter((v) =>
        (relation.contents || []).some((c) => (c.content_version_name || 'V1') === v)
      );
      const lastRelationScreenNames =
        assignedContent && isTheLastOfTheRelationship
          ? isTheLastOfTheRelationship(relation, assignedContent)
          : [];
      return {
        relation,
        covered: !!assignedContent,
        assignedContent,
        excludedContent,
        assignedVersions,
        screenCount: (relation.displays && relation.displays.length) || 0,
        lastRelationScreenNames,
      };
    });
  }, [listResolution, activeVersion, versionContents, versions, isTheLastOfTheRelationship]);

  const coveredCount = rows.filter((r) => r.covered).length;
  const pendingCount = rows.length - coveredCount;

  const filteredRows = rows
    .filter((r) => {
      if (tab === 'pending') return !r.covered;
      if (tab === 'covered') return r.covered;
      return true;
    })
    .filter((r) => {
      if (!search) return true;
      const label = `${r.relation.resolution_width}x${r.relation.resolution_height}`;
      return label.includes(search.replace(/\s/g, ''));
    });

  const counts = { all: rows.length, pending: pendingCount, covered: coveredCount };

  const versionPillStyle = {
    fontSize: 11,
    padding: '0 4px',
    borderRadius: 3,
    display: 'inline-block',
    maxWidth: 90,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    cursor: 'pointer',
    background: '#e6f4ff',
    color: '#1890ff',
  };

  return (
    <div className="resolutions-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{t('Resolutions')}</h3>
        <span>{coveredCount}/{rows.length}</span>
      </div>

      <div style={{ height: 4, background: '#f0f0f0', borderRadius: 2, marginBottom: 12 }}>
        <div
          style={{
            height: '100%',
            width: rows.length ? `${(coveredCount / rows.length) * 100}%` : '0%',
            background: '#52c41a',
            borderRadius: 2,
          }}
        />
      </div>

      {rows.length > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                padding: '4px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                background: tab === key ? '#1890ff' : '#fff',
                color: tab === key ? '#fff' : 'inherit',
                cursor: 'pointer',
              }}
            >
              {key === 'all' ? t('All') : key === 'pending' ? t('Pending resolutions') : t('Covered resolutions')} ({counts[key]})
            </button>
          ))}
          <Input
            placeholder={t('Search resolution')}
            prefix={<Icon type="search" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200, flex: '0 0 auto' }}
          />
        </div>
      )}

      {hasDifferentResolution && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, color: '#888', fontStyle: 'italic' }}>
          <Icon type="warning" style={{ color: '#faad14', marginTop: 2 }} />
          <span>{t('There are ads that do not adapt correctly to the resolutions. You can continue or select ads from closer resolutions.')}</span>
        </div>
      )}

      <div
        className="resolution-cards"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
          maxHeight: 560,
          overflowY: 'auto',
          padding: 2,
        }}
      >
        {filteredRows.map(({ relation, covered, assignedContent, excludedContent, assignedVersions, screenCount, lastRelationScreenNames }) => {
          const rowKey = `${relation.resolution_width}x${relation.resolution_height}`;
          const inlineVersions = assignedVersions.slice(0, MAX_INLINE_VERSION_PILLS);
          const overflowVersions = assignedVersions.slice(MAX_INLINE_VERSION_PILLS);

          const isPlaceholderContent = covered && assignedContent.isPlaceholder;
          const isInactiveContent = covered && !!assignedContent.isInactive;
          const accentColor = !covered ? '#bfbfbf' : (isInactiveContent ? '#bfbfbf' : (isPlaceholderContent ? '#faad14' : '#52c41a'));
          const cardBorderColor = !covered ? '#d9d9d9' : (isInactiveContent ? '#d9d9d9' : accentColor);
          const cardBackground = !covered ? '#fff' : (isInactiveContent ? '#fff' : (isPlaceholderContent ? '#fffbe6' : '#f6ffed'));

          return (
            <Tooltip key={rowKey} title={isInactiveContent ? t('This ad is inactive on all screens') : ''}>
            <div
              style={{
                border: `1px solid ${cardBorderColor}`,
                borderRadius: 6,
                background: cardBackground,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                opacity: isInactiveContent ? 0.6 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, fontWeight: 600, fontSize: 13 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {relation.resolution_width}x{relation.resolution_height}
                  </span>
                  <Icon type="desktop" theme="outlined" style={{ color: '#888', flexShrink: 0 }} />
                  {covered && assignedContent.differentResolution && (
                    <Tooltip title={t('Tolerance applied')}>
                      <Icon type="warning" style={{ color: '#faad14', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: accentColor,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {screenCount}
                </span>
              </div>

              {covered ? (
                <div
                  role={onPreviewContent ? 'button' : undefined}
                  tabIndex={onPreviewContent ? 0 : undefined}
                  onClick={() => onPreviewContent && onPreviewContent(assignedContent, relation)}
                  style={{
                    flex: 1,
                    minHeight: 100,
                    borderRadius: 4,
                    overflow: 'hidden',
                    cursor: onPreviewContent ? 'pointer' : 'default',
                  }}
                >
                  <PreviewFrameHolder>
                    <AspectRatioScreenContent relation={relation}>
                      {assignedContent.file_thumb && (
                        <img
                          src={convertImage(assignedContent.file_thumb)}
                          alt={assignedContent.name}
                          className="contentRectangleCardImage"
                          style={{
                            height: '100%',
                            objectFit: assignedContent.differentResolution ? 'contain' : 'cover',
                          }}
                        />
                      )}
                    </AspectRatioScreenContent>
                  </PreviewFrameHolder>
                </div>
              ) : (
                <Tooltip title={excludedContent ? t('Unlinked: {{name}}', { name: excludedContent.name }) : ''}>
                  <div style={{ flex: 1, minHeight: 100, borderRadius: 4, overflow: 'hidden' }}>
                    <PreviewFrameHolder>
                      <AspectRatioScreenContent relation={relation} className="rectangleNoContent" />
                    </PreviewFrameHolder>
                  </div>
                </Tooltip>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                  {covered ? (
                    <>
                      {inlineVersions.map((v) => (
                        <Tooltip key={v} title={t('Go to {{version}}', { version: v })}>
                          <span role="button" tabIndex={0} onClick={() => onSelectVersion(v)} style={versionPillStyle}>
                            {v}
                          </span>
                        </Tooltip>
                      ))}
                      {overflowVersions.length > 0 && (
                        <Dropdown
                          overlay={
                            <Menu>
                              {overflowVersions.map((v) => (
                                <Menu.Item key={v} onClick={() => onSelectVersion(v)}>
                                  {v}
                                </Menu.Item>
                              ))}
                            </Menu>
                          }
                          trigger={['click']}
                        >
                          <Icon type="ellipsis" style={{ cursor: 'pointer', color: '#888' }} onClick={(e) => e.stopPropagation()} />
                        </Dropdown>
                      )}
                    </>
                  ) : (
                    excludedContent && onRelinkContent && !isPaymentStatusApproved && (
                      <Tooltip title={t('Relink this ad to these screens')}>
                        <Icon
                          type="link"
                          style={{ cursor: 'pointer', color: '#1890ff' }}
                          onClick={() => onRelinkContent(excludedContent, relation)}
                        />
                      </Tooltip>
                    )
                  )}
                </span>

                {covered && onUnlinkContent && !isPaymentStatusApproved && (
                  lastRelationScreenNames.length > 0 ? (
                    <Tooltip title={t('It\'s the last ad on screen {{screenName}}; to delete it, first add another ad there', { screenName: lastRelationScreenNames.join(', ') })}>
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f5f5',
                          border: '1px solid #e8e8e8',
                          color: '#bfbfbf',
                          cursor: 'not-allowed',
                          flexShrink: 0,
                        }}
                      >
                        <Icon type="close" style={{ fontSize: 12 }} />
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('Unlink this ad from these screens')}>
                      <span
                        onClick={() => onUnlinkContent(assignedContent, relation)}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fff0f6',
                          border: '1px solid #ffadd2',
                          color: '#eb2f96',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Icon type="close" style={{ fontSize: 12 }} />
                      </span>
                    </Tooltip>
                  )
                )}
              </div>
            </div>
            </Tooltip>
          );
        })}
      </div>

      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: 12 }}>
        {rows.length > 1 ? (
          <span>{rows.reduce((sum, r) => sum + r.screenCount, 0)} {t('total screens')}</span>
        ) : (
          <span />
        )}
        {pendingCount > 0 ? (
          <span style={{ color: '#faad14' }}>
            <Icon type="warning" /> {pendingCount} {t('pending resolutions')}
          </span>
        ) : (
          <span style={{ color: '#52c41a' }}>
            <Icon type="check" /> {t('All assigned')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ResolutionsPanelComponent;
